import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/**
 * Geração de PDF de books que simulam folhas A4.
 *
 * Cada página é capturada individualmente e vira uma página inteira do PDF. Isso evita a
 * impressão nativa do navegador, que no iOS impõe margens, cabeçalho e rodapé e acaba
 * quebrando o layout das folhas.
 */

//LARGURA DA FOLHA A4 EM MILIMETROS
const A4_LARGURA_MM = 210;

/**
 * Erro usado quando o próprio usuário cancela a geração. É tratado de forma silenciosa
 * por quem chama, já que não representa uma falha.
 */
export class GeracaoCanceladaError extends Error {
    constructor() {
        super('Geração do PDF cancelada.');
        this.name = 'GeracaoCanceladaError';
    }
}

function verificarCancelamento(signal?: AbortSignal) {
    if (signal?.aborted) {
        throw new GeracaoCanceladaError();
    }
}

function esperar(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

//QUALIDADE DA COMPRESSÃO JPEG DAS PÁGINAS
const QUALIDADE_JPEG = 0.85;

/**
 * Converte o canvas em JPEG binário (Uint8Array).
 *
 * Usa toBlob no lugar de toDataURL: o data URL é uma string base64, ~33% maior que o
 * binário, e permanece em memória junto com a cópia que o jsPDF mantém. Em books longos
 * essa diferença é a maior fonte de consumo de RAM.
 */
function canvasParaBinario(canvas: HTMLCanvasElement, qualidade: number): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Não foi possível converter a página em imagem.'));
                    return;
                }

                blob.arrayBuffer()
                    .then((buffer) => resolve(new Uint8Array(buffer)))
                    .catch(reject);
            },
            'image/jpeg',
            qualidade
        );
    });
}

/**mede quanto vale 1mm em px neste dispositivo, ao invés de assumir 96 DPI */
function medirPxPorMm(): number {
    const regua = document.createElement('div');
    regua.style.cssText = 'width:100mm;position:absolute;visibility:hidden;pointer-events:none;';
    document.body.appendChild(regua);
    const px_por_mm = regua.offsetWidth / 100;
    document.body.removeChild(regua);

    return px_por_mm;
}

/**
 * Localiza as folhas do book: blocos com a largura de uma folha A4.
 *
 * A altura não é fixada em 297mm de propósito — há páginas mais altas (o sumário
 * interpretado, por exemplo). Cada página entra no PDF com a sua própria altura, então
 * nenhuma folha é descartada nem cortada.
 */
function obterPaginasDoBook(container: HTMLElement): HTMLElement[] {

    const largura_a4_px = A4_LARGURA_MM * medirPxPorMm();

    //tolerância generosa para absorver bordas e arredondamentos de layout
    const tolerancia = 6;

    const candidatos = Array.from(container.querySelectorAll<HTMLElement>('*')).filter((elemento) => {
        /**
         * usa offsetWidth/offsetHeight (tamanho de layout) e não getBoundingClientRect,
         * pois este último devolve o tamanho já com transform/zoom aplicado
         */
        const largura_compativel = Math.abs(elemento.offsetWidth - largura_a4_px) <= tolerancia;

        //ignora blocos sem altura real (wrappers colapsados)
        const tem_altura = elemento.offsetHeight > largura_a4_px / 4;

        return largura_compativel && tem_altura;
    });

    /**
     * descarta candidatos que estão dentro de outro candidato. Sem isso, uma página que
     * possua um wrapper interno da mesma largura seria capturada duas vezes no PDF.
     * A ordem do querySelectorAll é a do documento, então o ancestral sempre vem antes.
     */
    return candidatos.filter((elemento) => {
        return !candidatos.some((outro) => outro !== elemento && outro.contains(elemento));
    });
}

/**
 * As páginas costumam entrar no DOM de forma lazy (krigagens, mapas). Aqui aguardamos a
 * quantidade de páginas estabilizar antes de capturar, para não gerar um PDF incompleto
 * caso o usuário clique no botão logo que a tela abre.
 */
async function aguardarPaginasCarregarem(container: HTMLElement, signal?: AbortSignal): Promise<HTMLElement[]> {

    const TEMPO_MAXIMO_MS = 30000;
    const INTERVALO_MS = 400;
    const LEITURAS_ESTAVEIS_NECESSARIAS = 3;

    let paginas = obterPaginasDoBook(container);
    let leituras_estaveis = 0;
    let tempo_decorrido = 0;

    while (tempo_decorrido < TEMPO_MAXIMO_MS) {

        await esperar(INTERVALO_MS);
        tempo_decorrido += INTERVALO_MS;

        //permite abortar ainda durante a espera pelo carregamento das páginas
        verificarCancelamento(signal);

        const paginas_atuais = obterPaginasDoBook(container);

        if (paginas_atuais.length === paginas.length && paginas_atuais.length > 0) {
            leituras_estaveis++;

            if (leituras_estaveis >= LEITURAS_ESTAVEIS_NECESSARIAS) {
                return paginas_atuais;
            }
        }
        else {
            leituras_estaveis = 0;
        }

        paginas = paginas_atuais;
    }

    return paginas;
}

/**
 * Corrige o deslocamento dos contornos dos talhões (polígonos do Leaflet) na captura.
 *
 * O Leaflet posiciona o <svg> das camadas vetoriais com um transform CSS
 * (translate(-padding, -padding)) e usa um viewBox com a mesma origem para compensar.
 * Na tela os dois se anulam.
 *
 * O html2canvas trata o <svg> como elemento substituído: serializa o nó como imagem e a
 * desenha na caixa do elemento. Nesse caminho o transform não é aplicado como no
 * navegador, e os contornos saem deslocados — enquanto os tiles do satélite, que são
 * <img> comuns, continuam corretos.
 *
 * A correção troca o transform por posicionamento equivalente em left/top, que o
 * html2canvas interpreta corretamente. O viewBox é preservado: mexer nele desloca ainda
 * mais (verificado experimentalmente).
 *
 * IMPORTANTE: precisa rodar no clone montado pelo html2canvas (via onclone), e não no DOM
 * real — a renderização parte do clone, então alterações no elemento original não chegam
 * à imagem final.
 */
function corrigirVetoresLeaflet(documento: Document): void {

    const svgs = Array.from(documento.querySelectorAll<SVGSVGElement>('svg.leaflet-zoom-animated'));

    svgs.forEach((svg) => {

        const transform = documento.defaultView?.getComputedStyle(svg).transform;

        if (!transform || transform === 'none') return;

        const matriz = transform.match(/matrix\((.+)\)/);

        if (!matriz) return;

        const valores = matriz[1].split(',').map(Number);

        const deslocamento_x = valores[4];
        const deslocamento_y = valores[5];

        if (!Number.isFinite(deslocamento_x) || !Number.isFinite(deslocamento_y)) return;

        //converte o transform em left/top, que o html2canvas posiciona corretamente
        svg.style.transform = 'none';
        svg.style.position = 'absolute';
        svg.style.left = `${deslocamento_x}px`;
        svg.style.top = `${deslocamento_y}px`;
    });
}

interface GerarPdfProps {
    id_container: string;
    nome_arquivo: string;
    onTotalPaginas?: (total_paginas: number) => void;
    onProgresso?: (pagina_atual: number, total_paginas: number) => void;
    signal?: AbortSignal;
}

export async function gerarPdfDoBook({ id_container, nome_arquivo, onTotalPaginas, onProgresso, signal }: GerarPdfProps): Promise<void> {

    const container = document.getElementById(id_container);

    if (!container) {
        throw new Error('Não foi possível localizar o book para gerar o PDF.');
    }

    //as páginas entram no DOM de forma lazy, então espera o book parar de crescer
    const paginas = await aguardarPaginasCarregarem(container, signal);

    if (paginas.length === 0) {
        throw new Error('Nenhuma página do book foi encontrada. Aguarde o carregamento e tente novamente.');
    }

    onTotalPaginas?.(paginas.length);

    const px_por_mm = medirPxPorMm();

    let pdf: jsPDF | null = null;

    /**
     * o iOS tem limite rígido de memória por canvas, então em books longos reduzimos a escala
     * para não estourar e devolver um canvas em branco
     */
    const escala = paginas.length > 40 ? 1.5 : 2;

    for (let index = 0; index < paginas.length; index++) {

        const pagina = paginas[index];

        onProgresso?.(index + 1, paginas.length);

        /**
         * cede o controle ao navegador para que o React pinte o progresso ANTES de iniciar a
         * captura, que é síncrona e pesada. Sem isso a barra só avançaria ao final de tudo.
         * É também a brecha em que o clique em "Cancelar" consegue ser processado.
         */
        await esperar(0);

        //interrompe entre páginas: a captura em si não é cancelável no meio
        verificarCancelamento(signal);

        const canvas = await html2canvas(pagina, {
            scale: escala,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            logging: false,
            /**
             * fixa o tamanho de captura no tamanho de layout da página. O html2canvas clona
             * o nó num iframe isolado, então o scroll do container não corta a captura
             */
            width: pagina.offsetWidth,
            height: pagina.offsetHeight,
            windowWidth: pagina.offsetWidth,
            windowHeight: pagina.offsetHeight,
            scrollX: 0,
            scrollY: 0,
            /**
             * roda no clone (não no DOM real) logo antes da renderização, corrigindo a
             * posição dos contornos sem qualquer efeito colateral na tela do usuário
             */
            onclone: (documento_clonado) => {
                corrigirVetoresLeaflet(documento_clonado);
            }
        });

        /**
         * toBlob + Uint8Array no lugar de toDataURL: o data URL é uma string base64, ~33%
         * maior que o binário, e fica retida em memória junto com a cópia que o jsPDF guarda.
         * Em books longos essa diferença é a maior fonte de consumo de RAM.
         */
        const imagem = await canvasParaBinario(canvas, QUALIDADE_JPEG);

        /**
         * zera o canvas assim que a imagem é extraída. Sem isso, os bitmaps de todas as
         * páginas ficam vivos até o fim da geração — num book longo são vários GB
         */
        canvas.width = 0;
        canvas.height = 0;

        /**
         * cada folha entra com a sua própria altura, respeitando páginas fora do padrão
         * (o sumário interpretado, por exemplo, é mais alto que uma A4)
         */
        const largura_mm = pagina.offsetWidth / px_por_mm;
        const altura_mm = pagina.offsetHeight / px_por_mm;
        const orientacao = altura_mm >= largura_mm ? 'portrait' : 'landscape';

        if (pdf === null) {
            pdf = new jsPDF({
                orientation: orientacao,
                unit: 'mm',
                format: [largura_mm, altura_mm],
                //o conteúdo já é JPEG comprimido: recomprimir custa tempo sem reduzir o arquivo
                compress: false
            });
        }
        else {
            pdf.addPage([largura_mm, altura_mm], orientacao);
        }

        pdf.addImage(imagem, 'JPEG', 0, 0, largura_mm, altura_mm, undefined, 'FAST');
    }

    pdf?.save(`${nome_arquivo}.pdf`);
}
